import { useState, useEffect, useMemo } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

import Sidebar from '../components/shared/sidebar';
import PagePreview from './page-preview';
import SkeletonLoader from '../components/skeleton-loader';
import KitCard from '../components/kit-card';
import CustomSelect from '../components/custom-select';

import useTemplateManifest from '../hooks/use-template-manifest';
import { getFavorites, toggleFavorite } from '../utils/favorites';
import { highlightText } from '../utils/highlight';
import { TEMPLATE_CATEGORIES, SORT_OPTIONS} from '../constants/pages';
import { sortTemplates } from '../utils/sort-templates';

const PagesPage = ({ onInsertComplete ,isProActive, handleUpgradeClick}) => {
	const [selectedCategory, setSelectedCategory] = useState('all');
	const [searchQuery, setSearchQuery] = useState('');
	const [activePage, setActivePage] = useState(null);
	const [favorites, setFavorites] = useState([]);
	const [error, setError] = useState(null);
	const [sortBy, setSortBy] = useState('default');

	const {
		templates,
		loading,
		hasMore,
		loadMore,
		total
	} = useTemplateManifest('pages', null, searchQuery, setError);

	useEffect(() => {
		getFavorites().then(setFavorites);
	}, []);

	const handleToggleFavorite = async (id) => {
		const updated = await toggleFavorite(id);
		setFavorites(updated);
	};

	const normalizeCategory = (value) =>
		String(value || 'uncategorized').trim().toLowerCase();

	// Combine filtering + sorting
	const visibleTemplates = useMemo(() => {
		const currentCategory = normalizeCategory(selectedCategory);
		const seenIds = new Set();

		const filtered = templates.filter((tpl) => {
			const categories = Array.isArray(tpl.category)
				? tpl.category.map(normalizeCategory)
				: [normalizeCategory(tpl.category)];

			const matchesCategory =
				currentCategory === 'all' || categories.includes(currentCategory);

			if (!matchesCategory || seenIds.has(tpl.id)) return false;
			seenIds.add(tpl.id);
			return true;
		});

		return sortTemplates(filtered, sortBy);
	}, [templates, selectedCategory, sortBy]);

	const renderError = () => (
		<p className="vayu-error-state">{error}</p>
	);

	const renderEmpty = () => (
		<p className={`vayu-empty-state ${searchQuery ? 'has-query' : ''}`}>
			{searchQuery ? (
				<>
					<span className="vayu-empty-state__icon">🔍</span>
					{__('No results found for', 'vayu-blocks')}
					<span className="vayu-empty-state__highlight">"{searchQuery}"</span>
				</>
			) : (
				<>
					<span className="vayu-empty-state__icon">📁</span>
					{__('No templates available here yet.', 'vayu-blocks')}
				</>
			)}
		</p>
	);

	const renderTemplates = () => (
		<>
			<div className="vayu-template-toolbar">
				{total > 0 && selectedCategory === 'all' && (
					<div className="vayu-dl-template-count">
						{`Showing ${visibleTemplates.length} of ${total} templates`}
					</div>
				)}

				{visibleTemplates.length > 1 && (
					<CustomSelect
						value={sortBy}
						onChange={setSortBy}
						options={SORT_OPTIONS}
					/>
				)}
			</div>

			<div className={`vb-template-grid-pages vayu-template-grid ${ visibleTemplates.length > 0 ? 'fade-in-ready' : ''}`}>
				{visibleTemplates.map((tpl, index) => (
					<KitCard
						key={tpl.id}
						title={
							<span
								dangerouslySetInnerHTML={{
									__html: highlightText(tpl.title, searchQuery),
								}}
							/>
						}
						style={{ '--index': index }}
						preview={tpl.preview}
						premium={tpl.premium}
						onClick={() => setActivePage(tpl)}
						pattern={tpl?.pattern || false}
						isFavorite={favorites.includes(tpl.id)}
						onToggleFavorite={() => handleToggleFavorite(tpl.id)}
						isProActive={isProActive}
						handleUpgradeClick={handleUpgradeClick}
					/>
				))}
			</div>

			{hasMore && selectedCategory === 'all' && total > templates.length && (
				<div className="vayu-load-more-wrap">
					<button
						className="vayu-load-more-button"
						onClick={loadMore}
						disabled={loading}
					>
						{loading ? __('Loading...', 'vayu-blocks') : __('Load More', 'vayu-blocks')}
					</button>
				</div>
			)}
		</>
	);

	return (
		<>
			{activePage ? (
				<PagePreview
					kit={activePage}
					onBack={() => setActivePage(null)}
					onInsertComplete={onInsertComplete}
					isProActive={isProActive}
					handleUpgradeClick={handleUpgradeClick}
				/>
			) : (
				<>
					<Sidebar
						activeCategory={selectedCategory}
						onSelectCategory={setSelectedCategory}
						onSearch={setSearchQuery}
						tab="pages"
						categories={TEMPLATE_CATEGORIES['pages']}
					/>

					<section className="vayu-main-content">
						{loading && templates.length === 0
							? <SkeletonLoader count={6} />
							: error
							? renderError()
							: visibleTemplates.length === 0
							? renderEmpty()
							: renderTemplates()}
					</section>
				</>
			)}
		</>
	);
};

export default PagesPage;
